import React, { createContext, useContext, ReactNode } from 'react';
import { notification } from 'antd';
import type { NotificationArgsProps } from 'antd';

type NotificationType = 'success' | 'info' | 'warning' | 'error';
type NotificationPlacement = NotificationArgsProps['placement'];

// Definir el tipo del contexto que tendrá la función show para mostrar notificaciones
interface NotificationContextType {
    show: (
        type: NotificationType,
        title: string,
        description: ReactNode,
        placement?: NotificationPlacement
    ) => void;
}
// Crear el contexto para las notificaciones, inicialmente undefined
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Hook personalizado para acceder al contexto de notificación
export const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    // Si el contexto no se encuentra, lanzar un error para asegurarse que el proveedor está envuelto
    if (!context) {
        throw new Error('useNotificationContext debe usarse dentro del NotificationProvider');
    }
    return context;
};

// Interfaz para definir las props del NotificationProvider (componente que envuelve)
interface NotificationProviderProps {
    children: ReactNode;
}

// Definir el componente NotificationProvider, que envolverá la aplicación
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    // Utilizar la API de notificaciones de Ant Design
    const [api, contextHolder] = notification.useNotification();

    // Definir la función show que permite mostrar una notificación personalizada
    const show = (
        type: NotificationType,
        title: string,
        description: ReactNode,
        placement?: NotificationPlacement
    ) => {
        api[type]({
            message: title,
            description,
            placement,
        });
    };

    return (
        // Proveer la función show a todos los componentes envueltos y renderizar contextHolder globalmente
        <NotificationContext.Provider value={{ show }}>
            {contextHolder}{/* Esto asegura que las notificaciones se puedan mostrar */}
            {children}{/* Renderizar todos los hijos dentro del proveedor */}
        </NotificationContext.Provider>
    );
};
